<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import CheckModal from "@/components/CheckModal";
import DocumentActions from "@/components/document/DocumentActions.vue";
import acpmProcedureAPI from "@/services/standards/acpm/acpmProcedureAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";

const DEFAULT_OBJETIVO =
  "Establecer la metodología para identificar, analizar, implementar, verificar y cerrar las Acciones Correctivas, Preventivas y de Mejora (ACPM) derivadas del SG-SST, garantizando la eliminación de las causas de las no conformidades reales o potenciales y fortaleciendo la mejora continua mediante el ciclo PHVA.";
const DEFAULT_ALCANCE =
  "Aplica a todas las sedes, centros de trabajo, procesos, actividades, trabajadores dependientes, independientes, contratistas, subcontratistas, aprendices y visitantes de la organización, con independencia de su clasificación de riesgo (I a V).";
const DEFAULT_OBJ_ESP =
  "• Corregir oportunamente las no conformidades identificadas.\n• Eliminar las causas que originan incidentes, accidentes y enfermedades laborales.\n• Implementar acciones preventivas para evitar la materialización de riesgos.\n• Promover la mejora continua del SG-SST.\n• Cumplir los Estándares Mínimos de la legislación colombiana vigente.";

export default {
  page: { title: "Procedimiento ACPM" },
  components: { Layout, PageHeader, CheckModal, DocumentActions },
  data() {
    return {
      title: "7.1.2 Procedimiento de Acciones Correctivas, Preventivas y de Mejora",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "7.1.2 Medidas Correctivas, Preventivas y de Mejora", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      procedureId: this.$route.params.procedure || null,
      form: {
        version: "1.0",
        approval_date: new Date().toISOString().substring(0, 10),
        change_description: "Emisión inicial del procedimiento ACPM.",
        responsible: null,
        objetivo: DEFAULT_OBJETIVO,
        alcance: DEFAULT_ALCANCE,
        objetivos_especificos: DEFAULT_OBJ_ESP,
        observacion: null,
      },
      workflowStatus: "draft",
      signedAt: null,
      submitting: false,
      loading: true,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    procedureApi() {
      return {
        pdfUrl: (id) => acpmProcedureAPI.pdfUrl(id),
        sign: (id, payload) => acpmProcedureAPI.sign(id, payload),
      };
    },
  },
  methods: {
    goBack() { this.$router.go(-1); },
    load() {
      if (!this.procedureId) { this.loading = false; return; }
      acpmProcedureAPI.get(this.procedureId)
        .then((r) => {
          if (!r) { this.loading = false; return; }
          this.form.version = r.version || "1.0";
          this.form.approval_date = r.approval_date ? String(r.approval_date).substring(0, 10) : this.form.approval_date;
          this.form.change_description = r.change_description || null;
          this.form.responsible = r.responsible || null;
          this.form.objetivo = r.objetivo || DEFAULT_OBJETIVO;
          this.form.alcance = r.alcance || DEFAULT_ALCANCE;
          this.form.objetivos_especificos = r.objetivos_especificos || DEFAULT_OBJ_ESP;
          this.form.observacion = r.observacion || null;
          this.workflowStatus = r.workflow_status || "draft";
          this.signedAt = r.signed_at || null;
          this.loading = false;
        })
        .catch((e) => { console.log(e); this.loading = false; });
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const payload = { companies_id: this.companyID, ...this.form };
      const req = this.procedureId ? acpmProcedureAPI.update(this.procedureId, payload) : acpmProcedureAPI.save(payload);
      req
        .then((res) => {
          newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 58, status: "Pendiente" }).catch(() => {});
          Swal.fire({ title: "¡Éxito!", text: "Procedimiento guardado.", icon: "success", timer: 1600, showConfirmButton: false });
          this.submitting = false;
          if (!this.procedureId && res && res.id) {
            this.procedureId = res.id;
            this.$router.replace({ path: `/forms/estandares/${this.companyID}/acpm-procedimiento/${res.id}` });
            this.load();
          }
        })
        .catch((e) => { console.log(e); this.submitting = false; Swal.fire("Error", "No se pudo guardar el procedimiento", "error"); });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-9" v-if="!loading">
        <div class="card">
          <div class="card-body">
            <CheckModal>
              <p class="text-justify">
                Documento estructurado del procedimiento ACPM. El marco normativo, las definiciones, la metodología
                (7 etapas), las responsabilidades y los indicadores son texto fijo del procedimiento; aquí editas la
                identificación de la versión y las secciones de objetivo, alcance y objetivos específicos. Cada guardado
                es una versión que puede firmarse; usa "Nueva versión" en el listado para conservar el historial.
              </p>
            </CheckModal>

            <div v-if="isSigned" class="alert alert-success py-2 my-2 small">
              🔒 Esta versión ya fue firmada; los datos quedan bloqueados. Crea una "Nueva versión" desde el listado para
              registrar cambios en el tiempo.
            </div>

            <form @submit.prevent="save">
              <div class="form-row">
                <b-form-group class="col-md-2 px-2" label="Versión">
                  <b-form-input type="text" v-model="form.version" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Fecha de aprobación">
                  <b-form-input type="date" v-model="form.approval_date" :disabled="isSigned" required />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Responsable">
                  <b-form-input type="text" v-model="form.responsible" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-4 px-2" label="Descripción del cambio">
                  <b-form-input type="text" v-model="form.change_description" :disabled="isSigned" />
                </b-form-group>
              </div>

              <b-form-group label="1. Objetivo">
                <b-form-textarea v-model="form.objetivo" :disabled="isSigned" rows="3" max-rows="8" />
              </b-form-group>
              <b-form-group label="2. Alcance">
                <b-form-textarea v-model="form.alcance" :disabled="isSigned" rows="3" max-rows="8" />
              </b-form-group>
              <b-form-group label="3. Objetivos específicos (una línea por objetivo)">
                <b-form-textarea v-model="form.objetivos_especificos" :disabled="isSigned" rows="5" max-rows="12" />
              </b-form-group>
              <b-form-group label="Observaciones (opcional)">
                <b-form-input type="text" v-model="form.observacion" :disabled="isSigned" />
              </b-form-group>

              <p class="text-muted small">
                Secciones fijas incluidas automáticamente en el PDF: marco normativo, definiciones, fuentes de
                generación, metodología (7 etapas), matriz de control, responsabilidades, indicadores, criterios de
                eficacia y documentos relacionados.
              </p>

              <div class="text-right">
                <button type="submit" :disabled="isSigned || submitting" class="btn btn-primary mx-1">Guardar</button>
                <button type="button" class="btn btn-secondary mx-1" @click="goBack">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-3" v-if="!loading && procedureId">
        <h6 class="text-muted small mb-1">Procedimiento ACPM</h6>
        <DocumentActions
          :document-id="Number(procedureId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="procedureApi"
          document-title="Procedimiento de Acciones Correctivas, Preventivas y de Mejora"
          signer-role="gerente"
          @signed="onSigned"
        />
      </div>

      <div v-if="loading" class="col-12 text-center"><b-spinner variant="primary" label="Loading..."></b-spinner></div>
    </div>
  </Layout>
</template>
